import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * Sentinela no fim do menu. Enquanto ela não entra na área visível, há item abaixo da
 * dobra. O observer já desconta o recorte do contêiner que rola, então não importa se
 * quem rola é o `Box` (user agent de celular) ou o simplebar.
 */
export function NavEndSentinel({ onVisibilityChange }) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    // O observer pode entregar várias entradas do mesmo alvo num lote; a última é a atual.
    const observer = new IntersectionObserver((entries) =>
      onVisibilityChange(entries[entries.length - 1].isIntersecting)
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [onVisibilityChange]);

  return <Box ref={ref} aria-hidden sx={{ height: '1px', flexShrink: 0 }} />;
}

NavEndSentinel.propTypes = {
  onVisibilityChange: PropTypes.func.isRequired,
};

/**
 * Gradiente no rodapé da gaveta. No celular o `Scrollbar` não desenha barra de rolagem (ele
 * decide pelo user agent), e sem a pista o item abaixo da dobra parece não existir.
 */
export function NavScrollCue() {
  return (
    <Box
      aria-hidden
      data-testid="nav-scroll-cue"
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 56,
        pointerEvents: 'none',
        background: (theme) =>
          `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0)}, ${
            theme.palette.background.paper
          })`,
      }}
    />
  );
}
